import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { timeTrackingService, userService, searchCasesAsync } from '../../services/api';
import SearchableSelect from '../common/SearchableSelect';
import AsyncSearchableSelect from '../common/AsyncSearchableSelect';
import useModal from '../../hooks/useModal';

const ACTIVITY_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'Emails', label: 'Emails' },
  { value: 'Phone Call', label: 'Phone Call' },
  { value: 'Meeting', label: 'Meeting' },
  { value: 'Research', label: 'Research' },
  { value: 'Drafting', label: 'Drafting' },
  { value: 'Court Appearance', label: 'Court Appearance' },
  { value: 'Review', label: 'Review' },
  { value: 'Communications', label: 'Communications' },
  { value: 'Travel', label: 'Travel' },
  { value: 'Other', label: 'Other' },
];

const emptyRow = () => ({
  case_id: '',
  activity: '',
  description: '',
  hours: '',
  is_billable: true,
});

const BulkTimeEntryModal = ({ isOpen, onClose, onSuccess }) => {
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);
  const [firmUsers, setFirmUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState(() => Array.from({ length: 5 }, emptyRow));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setRows(Array.from({ length: 5 }, emptyRow));
    setSelectedUserId('');
    setError('');
    userService.getUsers().then(res => {
      const active = (res.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
      setFirmUsers(active);
    }).catch(() => setFirmUsers([]));
  }, [isOpen]);

  const updateRow = (index, field, value) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const removeRow = (index) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const addRows = (count) => {
    setRows(prev => [...prev, ...Array.from({ length: count }, emptyRow)]);
  };

  const filledRows = rows.filter(r => r.case_id && r.hours);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (filledRows.length === 0) return;
    setLoading(true);
    setError('');

    try {
      const entries = filledRows.map(r => {
        const desc = [r.activity, r.description].filter(Boolean).join(' - ');
        return {
          case_id: r.case_id,
          hours: parseFloat(r.hours),
          description: desc || null,
          is_billable: r.is_billable,
        };
      });
      const loggedAt = new Date(date + 'T12:00:00').toISOString();
      await timeTrackingService.createBulkEntries(entries, loggedAt, selectedUserId || null);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save entries');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-50 -z-10" aria-hidden="true" />
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[85vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Bulk Time Entry</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>
          )}

          {/* Date and User picker */}
          <div className="flex items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            <div className="w-64">
              <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
              <SearchableSelect
                value={selectedUserId}
                onChange={(val) => setSelectedUserId(val)}
                options={firmUsers.map(u => ({
                  value: u.id,
                  label: `${u.first_name} ${u.last_name}`
                }))}
                emptyOption="Myself (default)"
              />
            </div>
          </div>

          {/* Column headers */}
          <div className="grid grid-cols-[2fr_1fr_2fr_70px_36px_28px] gap-2 text-xs font-medium text-gray-500 uppercase px-1">
            <span>Case</span>
            <span>Activity</span>
            <span>Description</span>
            <span>Hours</span>
            <span title="Billable">Bill</span>
            <span />
          </div>

          {/* Entry rows */}
          <div className="space-y-2">
            {rows.map((row, index) => (
              <div key={index} className="grid grid-cols-[2fr_1fr_2fr_70px_36px_28px] gap-2 items-center">
                <AsyncSearchableSelect
                  value={row.case_id}
                  onChange={(val) => updateRow(index, 'case_id', val)}
                  onSearch={searchCasesAsync}
                  emptyOption="Select Case"
                />
                <select
                  value={row.activity}
                  onChange={(e) => updateRow(index, 'activity', e.target.value)}
                  className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  {ACTIVITY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={row.description}
                  onChange={(e) => updateRow(index, 'description', e.target.value)}
                  placeholder="Description"
                  className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={row.hours}
                  onChange={(e) => updateRow(index, 'hours', e.target.value)}
                  placeholder="0.0"
                  className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
                <div className="flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={row.is_billable}
                    onChange={(e) => updateRow(index, 'is_billable', e.target.checked)}
                    className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  className="text-gray-400 hover:text-red-500 p-1"
                  disabled={rows.length <= 1}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* Add row buttons */}
          <div className="flex gap-3 text-sm">
            <button
              type="button"
              onClick={() => addRows(1)}
              className="text-yellow-600 hover:text-yellow-700 flex items-center gap-1"
            >
              <Plus size={14} /> Add another
            </button>
            <button
              type="button"
              onClick={() => addRows(5)}
              className="text-yellow-600 hover:text-yellow-700"
            >
              Add 5 more rows
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t flex-shrink-0">
          <span className="text-sm text-gray-500">
            {filledRows.length} {filledRows.length === 1 ? 'entry' : 'entries'} ready
          </span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || filledRows.length === 0}
              className="px-4 py-2 text-sm text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : `Save ${filledRows.length} ${filledRows.length === 1 ? 'Entry' : 'Entries'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkTimeEntryModal;
